import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import GeoArt from "../components/GeoArt";
import { MaskedLines, Reveal, SectionHead } from "../components/Reveal";

const CHAPTERS = [
  { no: "01", title: "The Artist", body: "The artistic journey from self-taught creator to contemporary artist — years of painting in public, learning in community, and refining a visual language that became Field Faces.", seed: "ab-artist" },
  { no: "02", title: "The Curator", body: "Founder and Curator of the RISE Art Show — an annual fundraising exhibition during Tuskegee University Homecoming that turns art into direct support for undergraduate students.", seed: "ab-curator" },
  { no: "03", title: "The Community Builder", body: "Founder of The Collection, a cultural arts initiative building community through exhibitions, artist development, and cultural experiences.", seed: "ab-builder" },
  { no: "04", title: "The Founder", body: "Creator of DATOS Gallery Pass — a technology platform serving artists, collectors, curators, and galleries with tools for inventory, exhibitions, and acquisitions.", seed: "ab-founder" },
];

export default function About() {
  return (
    <main className="pt-24 sm:pt-32" data-testid="about-page">
      <section className="container-x section-y">
        <p className="eyebrow mb-6" data-testid="about-eyebrow">About</p>
        <MaskedLines lines={["Artist.", "Curator.", "Builder."]} className="h-display" />
        <Reveal delay={0.4}>
          <p className="mt-10 max-w-2xl text-muted-foreground text-base" data-testid="about-intro">
            Atuarra McCaslin is a contemporary artist whose work explores identity, memory, and the emotional complexity of the Black experience in America. Beyond the studio, he builds the systems — exhibitions, programs, and technology — that let culture sustain itself.
          </p>
        </Reveal>
      </section>

      <section className="border-t border-border">
        <div className="container-x section-y grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.no} delay={i * 0.06} className="bg-card">
              <div className="p-8 sm:p-12 h-full" data-testid={`about-chapter-${c.no}`}>
                <span className="font-mono-x text-xs text-[#FF1A1A]">{c.no}</span>
                <h3 className="font-display font-black uppercase text-2xl sm:text-3xl mt-3 mb-4">{c.title}</h3>
                <p className="text-sm text-muted-foreground max-w-md">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-x section-y grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="border border-border p-4 -rotate-1">
                <GeoArt seed="about-portrait" className="w-full" />
              </div>
              <p className="font-mono-x text-xs text-muted-foreground mt-4 uppercase tracking-widest">Portrait placeholder — studio, Atlanta</p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <SectionHead eyebrow="Biography" title="A Practice Built in Public" />
            <Reveal>
              <div className="space-y-5 text-muted-foreground text-sm sm:text-base max-w-2xl" data-testid="about-bio">
                <p>Atuarra McCaslin is a self-taught contemporary artist whose signature series, Field Faces, creates bold visual narratives inspired by street art, cartoon aesthetics, neo-expressionism, and community storytelling. His characters carry the full emotional range of the Black experience in America — grief and joy, weight and levity, memory and imagination.</p>
                <p>His commitment to culture extends far past the studio wall. As Founder, Curator, and Participating Artist of the RISE Art Show, he has built an annual fundraising exhibition during Tuskegee University Homecoming that channels the creative arts into real support for undergraduate students.</p>
                <p>Through The Collection, he develops exhibitions, artist programming, and cultural experiences that strengthen community. And through DATOS Gallery Pass, he is building technology that gives artists, collectors, curators, and galleries modern tools to manage the business of art.</p>
              </div>
            </Reveal>
            <SectionHead eyebrow="Artist Statement" title="The Field and the Faces" className="mt-16" />
            <Reveal>
              <blockquote className="border-l-4 border-[#FF1A1A] pl-6 text-base sm:text-lg max-w-2xl italic" data-testid="about-statement">
                "My work begins in the field — the real world of challenges, history, and lived experience — and ends in the faces: the identities and emotions we carry through it. I pull from street art and cartoons because those languages are honest, loud, and public. I'm not just making paintings. I'm building an ecosystem so the culture that inspires the work also benefits from it."
              </blockquote>
            </Reveal>
            <Reveal className="mt-10">
              <Link to="/artwork" className="btn-red" data-testid="about-view-work">See the Work <ArrowRight size={16} /></Link>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
